import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../utils/constants/api";
import { ApiResponse } from "../../../types/APIResponse";
import { FilterOperator, Ticket } from "../../../types";
import { Filters } from "./columnsSlice";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { LostStatuses } from "../../../types/LostStatuses";
import { STATUS_CODE } from "../../../utils/constants/auth";
import { ColumnName, ReportType } from "./columnTypes";
import { FoundStatuses } from "../../../types/FoundItemTypes";

function replaceLostWithFound(input: string | { [key: string]: any }): string | { [key: string]: any } {
    if (typeof input === 'string') {
        return input.replace(/lost/g, 'found');
    } else if (typeof input === 'object' && input !== null) {
        const newObj: { [key: string]: any } = {};
        for (const key in input) {
            if (input.hasOwnProperty(key)) {
                newObj[key] = replaceLostWithFound(input[key]);
            }
        }
        return newObj;
    }
    return input;
}

export interface FetchTicketsByStatusParams {
    status: number;
    offset: number;
    limit: number;
    filters: Filters;
    type: ReportType;
    order: Array<Object>;
    reason?: string;
    shipFilter?: boolean | null;
    deletionSource?: "" | "manual" | "auto-unpaid" | "auto-draft";
}

export const fetchTicketsByStatus = createAsyncThunk(
    "columns/fetchTicketsByStatus",
    async ({ status, offset, limit, filters, type, order, reason, shipFilter, deletionSource }: FetchTicketsByStatusParams, { rejectWithValue, getState }) => {
        // Snapshot the filter epoch at request-issue time. Fulfilled handler will reject the
        // response if the user has changed filters since (prevents stale data overwriting state).
        const epochAtIssue = (getState() as any)?.columns?.filterEpoch ?? 0;
        try {
            let response: ApiResponse<Ticket[]> & { counts: number } | any = [];
            if (type === ReportType.Lost && status === LostStatuses.Deleted) {
                const deletedParams: any = {
                    offset,
                    limit,
                    order,
                    withDeleted: true,
                    ...filters
                };
                // Only include reason if it's provided and not empty
                if (reason && reason !== "") {
                    deletedParams.reason = reason;
                }
                if (deletionSource) {
                    deletedParams.deletionSource = deletionSource;
                }
                response = await api.post(`${apiEndpoints.lost.getDeletedLostInfo}`,
                    {
                        data: deletedParams
                    }
                );
                response.data = response?.data?.map((ticket: Ticket) => ({
                    ...ticket || {},
                    isLostItem: true,
                }));
                response.counts = response?.counts || 0;
            } else {
                response = await api.post(`${apiEndpoints.lost.getLostItemInfoByStatus}`,
                    {
                        data: {
                            offset,
                            limit,
                            order,
                            status,
                            ...(shipFilter !== null && {isDelivered: shipFilter}),
                            ...filters
                        }
                    }
                );
            }
            return {
                status,
                tickets: response?.data,
                offset,
                counts: response?.counts,
                epoch: epochAtIssue,
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


export interface BatchColumnRequest {
    status: number;
    offset: number;
    limit: number;
    order: Array<Object>;
    type: ReportType;
    [key: string]: any;
}

export const fetchTicketsByStatusBatch = createAsyncThunk(
    "columns/fetchTicketsByStatusBatch",
    async ({ columns, filters }: { columns: BatchColumnRequest[]; filters: any }, { rejectWithValue, getState }) => {
        const epochAtIssue = (getState() as any)?.columns?.filterEpoch ?? 0;
        try {
            const response: any = await api.post(apiEndpoints.lost.getLostItemInfoByStatusBatch, {
                data: {
                    columns: columns.map(col => ({
                        status: col.status,
                        offset: col.offset,
                        limit: col.limit,
                        order: col.order,
                        ...filters,
                    })),
                },
            });
            if (!response?.success) {
                throw new Error(response?.message || "Failed to fetch batch tickets");
            }
            return {
                results: response.columns.map((r: any, i: number) => ({
                    status: r.status,
                    tickets: r.data || [],
                    offset: r.offset || 0,
                    counts: r.counts || 0,
                    type: columns[i].type,
                })),
                epoch: epochAtIssue,
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateLostItemStatus = createAsyncThunk(
    "columns/updateLostItemStatus",
    async ({ id, status, reason, unlinkReason, sendEmail, withDeleted, lfDepartmentDetails }: {
        id: string;
        status: number,
        reason?: string,
        unlinkReason?: string,
        sendEmail?: boolean,
        withDeleted?: boolean,
        lfDepartmentDetails?: any
    }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<any> = await api.put(`${apiEndpoints.reportDetail.updateLostItemStatus}/${id}`,
                { data: { status, reason, unlinkReason, sendEmail, withDeleted, lfDepartmentDetails } }
            );
            if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
                return {
                    data: response.data,
                    message: response.message,
                    emailSent: response.data?.emailSent || false
                };
            } else {
                throw new Error(response.message || "Failed to update lost item status.");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateLFNotifications = createAsyncThunk(
    "columns/updateLFNotifications",
    async ({ id, lfDepartmentDetails, sendEmail, emailLfDepartmentDetails }: {
        id: string;
        lfDepartmentDetails: any[];
        sendEmail?: boolean;
        emailLfDepartmentDetails?: any[];
    }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<any> = await api.put(
                `${apiEndpoints.reportDetail.updateLFNotifications}/${id}`,
                { data: { lfDepartmentDetails, sendEmail, emailLfDepartmentDetails } }
            );
            if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
                return {
                    data: response.data,
                    message: response.message,
                    emailSent: response.data?.emailSent || false
                };
            } else {
                throw new Error(response.message || "Failed to update LF notifications.");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateLFNotificationComment = createAsyncThunk(
    "columns/updateLFNotificationComment",
    async ({ id, index, comment, field }: { id: string; index: number; comment: string; field?: string }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<any> = await api.put(
                `${apiEndpoints.reportDetail.updateLFNotificationComment}/${id}`,
                { data: { index, comment, field } }
            );
            if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
                return { data: response.data };
            } else {
                throw new Error(response.message || "Failed to update comment.");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateLostItemDate = createAsyncThunk(
    "columns/updateLostItemDate",
    async ({ id, lostDate }: { id: string; lostDate: string }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.reportDetail.updateLostDate}/${id}`,
                { data: { lostDate } }
            );
            if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
                return {
                    lostDate,
                    id
                };
            } else {
                throw new Error(response.message || "Failed to update lost item date.");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateItemColumn = createAsyncThunk(
    "columns/updateItemColumn",
    async ({ columnStatus, name }: { columnStatus: string; name: string }, { rejectWithValue }) => {
        try {
            const response: ApiResponse<ColumnName> = await api.put(`${apiEndpoints.itemColumns.update}/${columnStatus}`,
                { data: { name } }
            );
            if (response?.success) {
                return response.data;
            } else {
                throw new Error(response.message || "Failed to update item column name.");
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getAllColumnNames = createAsyncThunk(
    "columns/getAllColumnNames",
    async (_, { rejectWithValue }) => {
        try {
            const response: ApiResponse<ColumnName[]> = await api.get(`${apiEndpoints.itemColumns.getAll}`);

            if (response?.success && response.data) {
                return response.data;
            } else {
                const errorMessage = response?.message || "Failed to get all column names.";
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || "An error occurred");
        }
    }
);
